import { OpportunityStage } from "../types";

export interface ActiveOpportunityContext {
  id: string;
  title: string;
  client: string;
  stage: OpportunityStage;
  updatedAt: number;
}

const STORAGE_KEY = "phq_active_opportunity";
const EVENT_NAME = "phq:active_opp_changed";

/**
 * Retrieves the currently active or last-opened opportunity context from localStorage.
 */
export const getActiveOpportunity = (): ActiveOpportunityContext | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ActiveOpportunityContext;
    if (parsed && typeof parsed.id === "string" && parsed.id.trim()) {
      return parsed;
    }
  } catch (err) {
    console.warn("Failed to read active opportunity from storage:", err);
  }

  return null;
};

/**
 * Sets the active opportunity context in localStorage and dispatches a notification event.
 */
export const setActiveOpportunity = (opp: {
  id: string;
  title: string;
  client: string;
  stage: OpportunityStage;
}): void => {
  if (typeof window === "undefined" || !opp.id) {
    return;
  }

  const context: ActiveOpportunityContext = {
    id: opp.id,
    title: opp.title,
    client: opp.client,
    stage: opp.stage,
    updatedAt: Date.now(),
  };

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(context));
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: context }));
  } catch (err) {
    console.warn("Failed to persist active opportunity:", err);
  }
};

/**
 * Clears the active opportunity context. If targetId is provided, only clears if matching.
 */
export const clearActiveOpportunity = (targetId?: string): void => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const current = getActiveOpportunity();
    if (targetId && current && current.id !== targetId) {
      return;
    }

    localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: null }));
  } catch (err) {
    console.warn("Failed to clear active opportunity:", err);
  }
};

/**
 * Subscribes to changes in the active opportunity across tabs and in-memory events.
 */
export const subscribeToActiveOpportunity = (
  callback: (active: ActiveOpportunityContext | null) => void,
): (() => void) => {
  if (typeof window === "undefined") {
    callback(null);
    return () => {};
  }

  // Initial emission
  callback(getActiveOpportunity());

  const handleCustomEvent = (e: Event) => {
    const custom = e as CustomEvent<ActiveOpportunityContext | null>;
    callback(custom.detail ?? null);
  };

  const handleStorageEvent = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) {
      callback(getActiveOpportunity());
    }
  };

  window.addEventListener(EVENT_NAME, handleCustomEvent);
  window.addEventListener("storage", handleStorageEvent);

  return () => {
    window.removeEventListener(EVENT_NAME, handleCustomEvent);
    window.removeEventListener("storage", handleStorageEvent);
  };
};
